import styled from "@emotion/styled";
import { motion } from "motion/react";
import {
  MDSIconButton,
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
  MDSTooltip,
  MDSIconKind,
  MDSActionMenuDropdown,
  MDSMenuContentList,
  MDSMenuDropdownSlotName,
} from "@/mds";
import { HeadsUpViewState } from "@/components/heads-up/context/heads-up-context";
import { MDSTooltipPlacement } from "@/mds/components/tooltip";
import { HeadsUpSidebarSubheader } from "@/components/heads-up/sidebar/header/heads-up-sidebar-subheader";
import { FC, useMemo, useCallback } from "react";
import { HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX } from "@/components/heads-up/constants";
import { useHeadsUpAnalytics } from "@/components/heads-up/analytics";

interface HeadsUpSidebarHeaderProps {
  viewState: HeadsUpViewState;
  formattedRelativeTime: string | null;
  headerTitle: string;
  onRefresh: () => void;
  canRefresh: boolean;
  moreMenuContent: MDSMenuContentList;
  activeNoteId: string | null;
}

export const HeadsUpSidebarHeader: FC<HeadsUpSidebarHeaderProps> = ({
  viewState,
  formattedRelativeTime,
  headerTitle,
  onRefresh,
  canRefresh,
  moreMenuContent,
  activeNoteId,
}) => {
  const analytics = useHeadsUpAnalytics();

  const shouldShowTitleText = useMemo(() => {
    if (viewState === HeadsUpViewState.FullEmpty) {
      return false;
    }

    if (viewState === HeadsUpViewState.Hydrating) {
      return false;
    }

    return true;
  }, [viewState]);

  const animationKey = useMemo(() => {
    return `${activeNoteId ?? "no-note"}`;
  }, [activeNoteId]);

  const handleRefreshClick = useCallback(() => {
    analytics.trackRefreshClick();
    onRefresh();
  }, [analytics, onRefresh]);

  return (
    <HeaderContainer>
      <Header>
        <HeaderLeft>
          {shouldShowTitleText ? (
            <AnimatedView
              key={animationKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <HeaderTitleText
                variant={MDSTextVariant.TextM}
                weight={MDSTextWeight.SemiBold}
                color={MDSTextColor.Contrast}
                overflow={MDSTextOverflow.Ellipsis}
                numberOfLines={1}
              >
                {headerTitle}
              </HeaderTitleText>
            </AnimatedView>
          ) : null}
          <HeadsUpSidebarSubheader
            viewState={viewState}
            formattedRelativeTime={formattedRelativeTime}
          />
        </HeaderLeft>
        <HeaderRight>
          <MDSTooltip
            config={{
              label: "Update",
              placement: MDSTooltipPlacement.Top,
            }}
          >
            <MDSIconButton
              iconKind={MDSIconKind.RotateRight}
              onClick={handleRefreshClick}
              isDisabled={!canRefresh}
            />
          </MDSTooltip>
          <MDSTooltip config={{ label: "More", placement: MDSTooltipPlacement.Top }}>
            <MDSActionMenuDropdown contentList={moreMenuContent}>
              <MDSIconButton slot={MDSMenuDropdownSlotName.Trigger} iconKind={MDSIconKind.More} />
            </MDSActionMenuDropdown>
          </MDSTooltip>
        </HeaderRight>
      </Header>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.div`
  height: ${HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX}px;
  min-height: ${HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX}px;
  max-height: ${HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX}px;
  min-width: 1px;
  display: block;
`;

const AnimatedView = styled(motion.div)`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing["4"]};
  padding-left: ${({ theme }) => theme.spacing["5"]};
  padding-right: ${({ theme }) => theme.spacing["3"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  min-height: ${HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX}px;
  height: ${HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX}px;
  max-height: ${HEADS_UP_SIDEBAR_HEADER_HEIGHT_PX}px;
`;

const HeaderTitleText = styled(MDSText)`
  margin-top: 2px;
  max-width: 100% !important;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;
